import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import { ApiMiddlewarePayload } from '@ocean/api'
import { localization } from '@ocean/configs'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { AppThunk } from 'store'
import { ApiAction, createApiAction } from 'api'
import { Person } from 'core/people/types'
import { userLoggedOut } from 'core/session/slices/session'
import { getListStudentLedEnrollments } from 'modules/simulatorTrainings/common/api/simulatorExerciseService'
import { namespace } from 'modules/simulatorTrainings/common/localization'
import { listStudentLedEnrollmentsLoaded } from 'modules/simulatorTrainings/common/slices/student'
import { ApiListStudentLedEnrollmentsResponse } from 'modules/simulatorTrainings/common/slices/student/types'
import { SimulatorStudentLedEnrolledPerson } from 'modules/simulatorTrainings/common/types/student'
import { SimulatorTrainingUtils } from 'modules/simulatorTrainings/common/utils'
import {
  getListSimulatorExerciseSchedules,
  addSimulatorExerciseSchedule,
  deleteSimulatorExerciseSchedule,
  updateSimulatorExerciseSchedule,
} from 'modules/simulatorTrainings/instructor/studentLed/Schedules/api/schedulesService'
import schedulesRoutes from 'modules/simulatorTrainings/instructor/studentLed/Schedules/routing/routes'
import {
  ScheduleStatus,
  ShowType,
  SchedulesTagFilterItem,
  SchedulesTagFilterItems,
  AddSimulatorExerciseScheduleModel,
  UpdateSimulatorExerciseScheduleModel,
} from 'modules/simulatorTrainings/instructor/studentLed/Schedules/types'
import trainingDetailsRoutes from 'modules/simulatorTrainings/instructor/studentLed/TrainingDetails/routing/routes'
import {
  SchedulesState,
  ApiSchedulesListResponse,
  ApiCreateSimulatorExerciseScheduleResponse,
} from './types'

const { i18n } = localization

const I18_PATH = `${namespace}:instructor.studentLed.schedules`

const initialState: SchedulesState = {
  scheduleListData: {},
  schedulesTagFilters: {
    status: { keyType: FilterKeyType.Number, value: ScheduleStatus.Upcoming },
    showType: { keyType: FilterKeyType.Number, value: ShowType.Own },
  },
  scheduleCreatorData: {
    addedStudents: {},
  },
  createSimulatorExerciseScheduleRequested: {},
  scheduleEditorData: {
    addedStudents: {},
  },
  updateSimulatorExerciseScheduleRequested: {},
  deleteScheduleRequested: {},
}

const schedulesSlice = createSlice({
  name: 'simulatorTrainingInstructorSLSchedules',
  initialState,
  reducers: {
    scheduleListLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSchedulesListResponse>>
    ) {
      state.scheduleListData = action.payload
    },
    clearScheduleList(state) {
      state.scheduleListData = initialState.scheduleListData
    },
    setSchedulesTagFilters(state, action: PayloadAction<SchedulesTagFilterItem>) {
      const filter = state.schedulesTagFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
    clearScheduleCreatorData(state) {
      state.scheduleCreatorData = initialState.scheduleCreatorData
    },
    setCreatorStartDate(state, action: PayloadAction<Date | undefined>) {
      state.scheduleCreatorData.startDate = action.payload
    },
    setCreatorEndDate(state, action: PayloadAction<Date | undefined>) {
      state.scheduleCreatorData.endDate = action.payload
    },
    setCreatorMaximumNumberOfAttempts(state, action: PayloadAction<number | undefined>) {
      state.scheduleCreatorData.maximumNumberOfAttempts = action.payload
    },
    removeCreatorStudents(state, action: PayloadAction<Record<string, Person>>) {
      Object.keys(action.payload).forEach(studentId => {
        delete state.scheduleCreatorData.addedStudents[studentId]
      })
    },
    addCreatorStudents(state, action: PayloadAction<Record<string, Person>>) {
      state.scheduleCreatorData.addedStudents = {
        ...state.scheduleCreatorData.addedStudents,
        ...action.payload,
      }
    },
    createSimulatorExerciseScheduleRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCreateSimulatorExerciseScheduleResponse>>
    ) {
      state.createSimulatorExerciseScheduleRequested = action.payload
    },
    clearScheduleEditorData(state) {
      state.scheduleEditorData = initialState.scheduleEditorData
    },
    setEditorStartDate(state, action: PayloadAction<Date | undefined>) {
      state.scheduleEditorData.startDate = action.payload
    },
    setEditorEndDate(state, action: PayloadAction<Date | undefined>) {
      state.scheduleEditorData.endDate = action.payload
    },
    setEditorMaximumNumberOfAttempts(state, action: PayloadAction<number | undefined>) {
      state.scheduleEditorData.maximumNumberOfAttempts = action.payload
    },
    setEditorStudents(
      state,
      action: PayloadAction<Record<string, SimulatorStudentLedEnrolledPerson>>
    ) {
      state.scheduleEditorData.addedStudents = action.payload
    },
    removeEditorStudents(
      state,
      action: PayloadAction<Record<string, SimulatorStudentLedEnrolledPerson>>
    ) {
      Object.keys(action.payload).forEach(studentId => {
        delete state.scheduleEditorData.addedStudents[studentId]
      })
    },
    addEditorStudents(
      state,
      action: PayloadAction<Record<string, SimulatorStudentLedEnrolledPerson>>
    ) {
      state.scheduleEditorData.addedStudents = {
        ...state.scheduleEditorData.addedStudents,
        ...action.payload,
      }
    },
    deleteSimulatorExerciseScheduleRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.deleteScheduleRequested = action.payload
    },
    updateSimulatorExerciseScheduleRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.updateSimulatorExerciseScheduleRequested = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  scheduleListLoaded,
  clearScheduleList,
  setSchedulesTagFilters,
  clearScheduleCreatorData,
  setCreatorStartDate,
  setCreatorEndDate,
  setCreatorMaximumNumberOfAttempts,
  removeCreatorStudents,
  addCreatorStudents,
  createSimulatorExerciseScheduleRequested,
  clearScheduleEditorData,
  setEditorStartDate,
  setEditorEndDate,
  setEditorMaximumNumberOfAttempts,
  setEditorStudents,
  removeEditorStudents,
  addEditorStudents,
  deleteSimulatorExerciseScheduleRequested,
  updateSimulatorExerciseScheduleRequested,
} = schedulesSlice.actions
export {
  clearScheduleList,
  setSchedulesTagFilters,
  clearScheduleCreatorData,
  setCreatorStartDate,
  setCreatorEndDate,
  setCreatorMaximumNumberOfAttempts,
  removeCreatorStudents,
  addCreatorStudents,
  clearScheduleEditorData,
  setEditorStartDate,
  setEditorEndDate,
  setEditorMaximumNumberOfAttempts,
  removeEditorStudents,
  addEditorStudents,
}
export default schedulesSlice.reducer

export const loadExerciseScheduleListData = (
  filters: SchedulesTagFilterItems,
  exerciseId: Guid
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSimulatorExerciseSchedules(filters, exerciseId),
    },
    payloadAction: scheduleListLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ simulatorTrainingInstructorSLSchedules }) =>
      simulatorTrainingInstructorSLSchedules.scheduleListData.result,
  })
}

export const getCreatorAddedStudents = createSelector(
  (addedStudents: Record<string, Person>) => addedStudents,
  addedStudents => Object.values(addedStudents)
)

export const getCreatorStudentEnrollments = createSelector(
  (addedStudents: Record<string, Person>) => addedStudents,
  addedStudents => Object.keys(addedStudents)
)

export const getCurrentEditorStartDate = createSelector(
  (editorStartDate: Date | undefined, startDate: string | undefined) => ({
    editorStartDate,
    startDate,
  }),
  ({ editorStartDate, startDate }) =>
    editorStartDate || (startDate ? new Date(startDate) : undefined)
)

export const getCurrentEditorEndDate = createSelector(
  (editorEndDate: Date | undefined, endDate: string | undefined) => ({ editorEndDate, endDate }),
  ({ editorEndDate, endDate }) => editorEndDate || (endDate ? new Date(endDate) : undefined)
)

const handleCreateSimulatorExerciseSchedule =
  (response: ApiMiddlewarePayload, params: { exerciseId: string }): AppThunk =>
  async dispatch => {
    const { exerciseId } = params
    const { error, result } = response
    if (!error && result) {
      dispatch(push(`${schedulesRoutes.list}/${exerciseId}`))
    }
    dispatch(createSimulatorExerciseScheduleRequested(response))
  }

export const createSimulatorExerciseSchedule = (
  params: AddSimulatorExerciseScheduleModel
): ApiAction => {
  const { exerciseId } = params
  return createApiAction({
    apiCalls: {
      request: () => addSimulatorExerciseSchedule(params),
    },
    payloadAction: createSimulatorExerciseScheduleRequested,
    nextAction: {
      action: handleCreateSimulatorExerciseSchedule,
      params: { exerciseId },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.scheduleCreator.successBook`),
  })
}

export const getEditorAddedStudents = createSelector(
  (addedStudents: Record<string, SimulatorStudentLedEnrolledPerson>) => addedStudents,
  addedStudents => Object.values(addedStudents)
)

export const getEditorStudentEnrollments = createSelector(
  (addedStudents: Record<string, SimulatorStudentLedEnrolledPerson>) => addedStudents,
  addedStudents => Object.keys(addedStudents)
)

export const getIdsOfStudentsWithAssessment = createSelector(
  (students: SimulatorStudentLedEnrolledPerson[] = []) => students,
  students => {
    const assessNames = SimulatorTrainingUtils.getAssessmentNames()
    return students
      .filter(({ status }) => status !== null && assessNames[status])
      .map(({ userId }) => userId)
  }
)

const handleSetDefaultEnrolledStudents =
  (response: ApiMiddlewarePayload<ApiListStudentLedEnrollmentsResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result) {
      const { enrollments } = result.list
      let addedStudents: Record<string, SimulatorStudentLedEnrolledPerson> = {}

      enrollments.forEach(person => {
        addedStudents[person.userId] = person
      })

      dispatch(setEditorStudents(addedStudents))
    }
    dispatch(listStudentLedEnrollmentsLoaded(response))
  }

export const loadScheduleEnrolledPeople = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListStudentLedEnrollments(scheduleId),
    },
    payloadAction: listStudentLedEnrollmentsLoaded,
    nextAction: handleSetDefaultEnrolledStudents,
  })
}

const handleUpdateSimulatorExerciseSchedule =
  (response: ApiMiddlewarePayload, params: { scheduleId: Guid }): AppThunk =>
  async dispatch => {
    const { scheduleId } = params
    const { error, result } = response
    if (!error && result) {
      dispatch(push(`${trainingDetailsRoutes.root}/${scheduleId}`))
    }
    dispatch(updateSimulatorExerciseScheduleRequested(response))
  }

const handleRedirectOnDeleteSchedule =
  (response: ApiMiddlewarePayload): AppThunk =>
  async (dispatch, getState) => {
    const { error } = response
    if (!error) {
      const { id: exerciseId } =
        getState().simulatorTrainingStudent.exerciseDetailsData.result?.details || {}
      dispatch(push(`${schedulesRoutes.list}/${exerciseId}`))
    }
    dispatch(deleteSimulatorExerciseScheduleRequested(response))
  }

export const deleteSchedule = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => deleteSimulatorExerciseSchedule(scheduleId),
    },
    payloadAction: deleteSimulatorExerciseScheduleRequested,
    nextAction: handleRedirectOnDeleteSchedule,
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const updateExerciseSchedule = (params: UpdateSimulatorExerciseScheduleModel): ApiAction => {
  const { scheduleId } = params
  return createApiAction({
    apiCalls: {
      request: () => updateSimulatorExerciseSchedule(params),
    },
    payloadAction: updateSimulatorExerciseScheduleRequested,
    nextAction: {
      action: handleUpdateSimulatorExerciseSchedule,
      params: { scheduleId },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.scheduleEditor.successUpdate`),
  })
}
